/**
 * The ONE Sentry seam for src/data/.
 *
 * Every other module in this directory is deliberately import-free and
 * dependency-injected (rpc.js's "NOTHING IS IMPORTED EXCEPT THE CLASSIFIER",
 * resync.js's "free of any Supabase import"), and that property is worth
 * keeping. So instead of six modules each growing their own `import * as
 * Sentry` and their own idea of what a field is called, they import this.
 *
 * ===========================================================================
 * WHY THIS IS A HELPER AND NOT SIX `Sentry.withScope` BLOCKS
 * ===========================================================================
 * Three reasons, in order of how expensive getting them wrong is:
 *
 *   1. FIELD NAMES. The audit-logging schema wants `component` / `action` /
 *      `org.id` on every event so one query spans the whole layer. Six
 *      hand-written scopes drift into `orgId`, `org_id` and `org` within a
 *      release — which is exactly finding F10: the one Sentry call the
 *      migration shipped put its correlation fields somewhere unqueryable.
 *   2. TAGS vs EXTRA. Sentry INDEXES tags and does not index extra/context.
 *      "which orgs saw this?" is a tag question; it is unanswerable if the org
 *      id went into `extra`. The split is decided here, once.
 *   3. TELEMETRY MUST NEVER THROW. A failure inside observability that takes
 *      down a save is strictly worse than the blind spot it was closing.
 *
 * ===========================================================================
 * ORG ID IS TAGGED EXPLICITLY EVEN THOUGH A GLOBAL TAG EXISTS
 * ===========================================================================
 * The provider already calls `Sentry.setTag("orgId", org)`. That is the org the
 * user is looking at NOW. It is not necessarily the org a failing call was FOR:
 * an org switch leaves the previous org's requests in flight, and accessGuard's
 * whole `forOrg` argument exists because those late refusals arrive after the
 * global has moved on. So each event carries the org it is actually about.
 *
 * ===========================================================================
 * LEVELS
 * ===========================================================================
 * `info`, `warning`, `error`. NOT `debug` — audit-logging check 9 rules it out,
 * and a debug breadcrumb is invisible anyway unless some later capture happens
 * to carry it, which in this layer nothing did.
 */
import * as Sentry from "@sentry/react";
import { createCapture } from "./captureFault.js";

/**
 * One structured event for one data-layer fault.
 *
 * The scope shape lives in `./captureFault.js` and is shared with the reports
 * Function's `@sentry/node` binding, so a Sentry search written against this
 * layer finds the server's events too. Only the SDK differs.
 *
 * `fault` is the RAW error where there is one — a real Error keeps its stack, so
 * Sentry groups by where it actually came from rather than by this file. A
 * string is used only where there is no error object (a stuck channel, a
 * sequence gap): those group by message, and their messages are deliberately
 * constant so they group into one issue.
 *
 * @param {Error|string} fault
 * @param {object} ctx
 * @param {string} ctx.component  The module: "rpc" | "realtime" | "refdata" | …
 * @param {string} ctx.action     The operation: "resync" | "org_bootstrap" | …
 * @param {string|null} [ctx.orgId]
 * @param {"info"|"warning"|"error"} [ctx.level]
 * @param {Record<string, string|number|boolean|null|undefined>} [ctx.tags]
 *   INDEXED. Searchable and groupable — correlation ids, codes, verdicts.
 *   Keep the value set bounded-ish; a tag is a dimension, not a payload.
 * @param {Record<string, unknown>} [ctx.context]
 *   NOT indexed. The rest of the wide event — counts, sequences, topics. One
 *   card named "data" on every event so the shape is the same everywhere.
 */
export const captureDataFault = createCapture(Sentry, {
  defaultComponent: "data",
  unknownFault: "unknown data-layer fault",
});

/**
 * A breadcrumb for churn that is NOT itself an event — a channel bouncing, a
 * basis lookup falling back. Breadcrumbs are free and only ever transmitted
 * attached to a capture, so they are the right home for "what was happening
 * just before" and the wrong home for anything anyone needs to query.
 */
export function dataBreadcrumb(category, message, data) {
  try {
    Sentry.addBreadcrumb({ category, level: "info", message, data });
  } catch {
    // Same reason as above.
  }
}
